import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, QueryClientProvider } from '@tanstack/react-query';
import getCSRFToken from './utils/CSRFToken';
import toast from 'react-hot-toast';

import { queryClient } from '../application';

const fetchBuildStatus = async () => {
  const response = await fetch(`/${process.env.PATH_PREFIX}/api/v1/site/build_status.json`);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
};

const createBuild = async () => {
  const requestOptions = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getCSRFToken() },
    body: JSON.stringify({}),
    credentials: 'same-origin',
  };
  const response = await fetch(`/${process.env.PATH_PREFIX}/api/v1/site/build.json`, requestOptions);
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response.json();
};

function Button({ pollingEnabled = true }) {
  const [isBuildStarted, setIsBuildStarted] = useState(false);

  const queryClient = useQueryClient();

  const { data, isError } = useQuery({
    queryKey: ['buildStatus'],
    queryFn: fetchBuildStatus,
    refetchInterval: pollingEnabled ? 10000 : false, // 10 seconds
  });

  const { mutate: triggerBuild, isPending: isBuildTriggering } = useMutation({
    mutationFn: createBuild,
    onSuccess: (data) => {
      if (data?.build_status === 'started') {
        setIsBuildStarted(true);
        toast.success('Build started successfully');
      }
    },
    onError: () => {
      toast.error('Failed to trigger build');
    },
    onSettled: () => {
      // Invalidate and refetch the build status query immediately after triggering a build
      queryClient.invalidateQueries({ queryKey: ['buildStatus'] });
    },
  });

  // Derive state from query data
  const isUserBuildPending = data?.user_build && !data.user_build.complete && !data.user_build.skipped;
  const isUserBuildQueued = data?.user_build?.queued;
  const isOtherBuildPending = data && !data?.current_build?.complete && !data?.user_build?.is_current;
  const isComplete = data?.user_build?.is_current && data?.user_build?.complete;

  const isPending = isBuildTriggering || isBuildStarted || isUserBuildPending || isUserBuildQueued;

  return (
    <div id="build-button" className="mb-3">
      <button className="btn btn-primary w-100" onClick={() => triggerBuild()} disabled={isPending}>
        {isPending ? (
          <>
            <span className="spinner-border spinner-border-sm me-3" />
            Publishing
          </>
        ) : (
          <>Publish Changes</>
        )}
      </button>
      {isOtherBuildPending && !isPending && (
        <small className="text-muted d-flex align-items-center justify-content-center mt-2">
          <span className="spinner-border spinner-border-sm me-3"></span>
          <span>Publishing in progress</span>
        </small>
      )}
      {isComplete && (
        <small className="text-muted d-flex align-items-center justify-content-center mt-2">
          <span className="fe fe-check me-3"></span>
          <span>Publishing complete!</span>
        </small>
      )}
      {isError && (
        <small className="text-danger d-flex align-items-center justify-content-center mt-2">
          <span>Error fetching build status.</span>
        </small>
      )}
    </div>
  );
}

export default function BuildButton(props) {
  return (
    <QueryClientProvider client={queryClient}>
      <Button {...props} />
    </QueryClientProvider>
  );
}
